import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AppCode, appUrl, isAppCode } from './appRegistry';
import { hasModule, useEntitlementsStore } from './entitlements';
import { useSessionStore } from './sessionStore';

export interface ProtectedRouteProps {
  children: ReactNode;
  /** in-app login path (legacy same-app login). Ignored when `loginUrl` is set. */
  loginPath?: string;
  /**
   * FE-6: absolute URL of the DEDICATED login app (e.g. https://zercode.uk). When set, a guest
   * is redirected there cross-origin with `?return=<the URL they wanted>` (the login app validates it
   * and, after auth, sends them back — the session follows via the httpOnly cookie).
   */
  loginUrl?: string;
  /** rendered while session status is still 'unknown' (e.g. refresh in flight) */
  fallback?: ReactNode;
  /** C-8: also require a licensed module — checked AND with auth [STD-API-6] */
  requiredModule?: string;
  /** C-8: shown when authenticated but the tenant's plan lacks requiredModule (license upsell) */
  upsell?: ReactNode;
  /**
   * SURF-GATE: the SPA this router IS. Set it at each app's root route and an authenticated user
   * whose roles do not reach this surface is bounced to one they do — see `surfaceRedirect`.
   */
  requiredApp?: AppCode;
}

/**
 * SURF-GATE — where to send someone who landed in the wrong SPA, or null to leave them alone.
 *
 * ⚠ AN EMPTY `reachableApps` MEANS "NO ANSWER", NOT "NO ACCESS", AND MUST FAIL OPEN. Three ways it
 * comes back empty with the user perfectly legitimate: a role with `role_type IS NULL` contributes no
 * surface at all (mig 315 leaves permission-only roles untyped ON PURPOSE, and `RoleTypes.SurfaceOf`
 * returns null for them), a backend too old to send the field at all (the Zod schema defaults it to
 * `[]`), and any future surface this registry has not learned yet. Denying on empty would lock a
 * tenant admin out of the admin console — strictly worse than the chrome leak this gate exists to
 * close, since the API 403s every unpermitted call either way. So the gate only ever ACTS on a
 * DEFINITE answer that excludes this app.
 *
 * ⚠ THE RESULT IS NEVER `requiredApp`, WHICH IS WHAT MAKES THIS LOOP-FREE: we redirect only when
 * `requiredApp` is absent from `reachable`, and only ever TO a member of `reachable`. The destination
 * SPA's own gate therefore passes, so no marker or hop counter is needed to break a ping-pong.
 */
function surfaceRedirect(requiredApp: AppCode, reachable: readonly string[], defaultApp: string | null | undefined): string | null {
  const distinct = reachable.filter(isAppCode);
  if (distinct.length === 0 || distinct.includes(requiredApp)) {
    return null; // fail open (see above), or they belong here
  }
  // RSU: the server's own landing choice wins when it is one they can reach — same rule the login
  // origin applies in `resolveAuthOriginRoute`, so "log in" and "type the wrong host" agree.
  const target = defaultApp && isAppCode(defaultApp) && distinct.includes(defaultApp) ? defaultApp : distinct[0]!;
  return appUrl(target);
}

/**
 * Route guard: renders children for an authenticated session, and — when `requiredModule` is set
 * — only if the tenant's plan includes that module (else `upsell`). Guests go to login: either the
 * dedicated login app cross-origin (`loginUrl`, FE-6) or the legacy in-app `loginPath`. License
 * gating is UX; the backend still enforces 403.
 *
 * SURF-GATE — `requiredApp` adds the third dimension: WHICH SPA the session's roles are for. The
 * login origin has always refused to route someone into an app they cannot reach
 * (`resolveAuthOriginRoute`), but that only guarded the front door: the refresh cookie is scoped
 * `Domain=zercode.uk` and therefore spans every persona subdomain, so a student with a live session
 * could type the admin host and get the admin shell — empty menu, dashboard 403s, but the whole
 * console's chrome and route surface. This closes that door in the SPA itself.
 *
 * ⚠ THIS IS UX AND ATTACK-SURFACE REDUCTION, NOT AUTHORIZATION, and there is no server-side version
 * of it to add later: a request carries no trustworthy signal of WHICH SPA sent it (an
 * `X-App-Surface` header would be client-controlled). `AppSurfaces` says the same thing twice on the
 * backend — a surface is not a grant. Per-endpoint permissions stay the only real gate [STD-API-6].
 */
export function ProtectedRoute({
  children,
  loginPath = '/login',
  loginUrl,
  fallback = null,
  requiredModule,
  upsell = null,
  requiredApp
}: ProtectedRouteProps) {
  const status = useSessionStore((s) => s.status);
  const user = useSessionStore((s) => s.user);
  const modules = useEntitlementsStore((s) => s.modules);
  const location = useLocation();
  const isGuest = status === 'guest';

  // SURF-GATE: computed during render so the wrong-surface branch can render `fallback` instead of
  // the children — the effect below only performs the navigation. `status === 'authenticated'`
  // implies `user !== null` (the store sets both in `setSession`), and the `?? []` keeps the
  // impossible state failing open rather than blank.
  const wrongSurface =
    status === 'authenticated' && requiredApp !== undefined
      ? surfaceRedirect(requiredApp, user?.reachableApps ?? [], user?.defaultApp)
      : null;

  // FE-6: cross-origin redirect to the dedicated login app, preserving the intended URL as `return`.
  // Done in an effect (side effect, not during render); the guard renders `fallback` meanwhile.
  useEffect(() => {
    if (isGuest && loginUrl) {
      const url = new URL(loginUrl);
      url.searchParams.set('return', window.location.href);
      window.location.assign(url.toString());
    }
  }, [isGuest, loginUrl]);

  // SURF-GATE: the bounce. Cross-origin, so it is `location.assign` and not a router navigate.
  // ⚠ The path is DROPPED on purpose — a deep link into the admin console means nothing in the
  // student SPA. The destination's own <LandingRedirect /> resolves the tenant's landing page.
  useEffect(() => {
    if (wrongSurface) {
      window.location.assign(wrongSurface);
    }
  }, [wrongSurface]);

  if (status === 'unknown') return <>{fallback}</>;
  if (isGuest) {
    // dedicated login app (FE-6): redirected by the effect above; render fallback while it happens.
    if (loginUrl) return <>{fallback}</>;
    // legacy in-app login: react-router navigate with the intended location in router state.
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }
  // SURF-GATE: never paint an app the session is not for, not even for the frame the redirect takes.
  if (wrongSurface) return <>{fallback}</>;
  // License check: wait for entitlements to load (modules !== null) before denying, so a licensed
  // route never flashes the upsell on first paint.
  if (requiredModule && modules !== null && !hasModule(modules, requiredModule)) {
    return <>{upsell}</>;
  }
  return <>{children}</>;
}

/** inverse guard for the login page: authenticated users skip it */
export function GuestRoute({ children, homePath = '/' }: { children: ReactNode; homePath?: string }) {
  const status = useSessionStore((s) => s.status);
  if (status === 'authenticated') return <Navigate to={homePath} replace />;
  return <>{children}</>;
}
